import { useMemo, useState } from 'react'

import { Box, Input, Text } from '@chakra-ui/react'
import { Button, Menu } from '@saas-ui/react'

import { ClaudeLogo } from '#components/logos/claude-logo.tsx'
import { OpenCodeLogo } from '#components/logos/open-code.tsx'
import { OpenAILogo } from '#components/logos/openai-logo.tsx'
import { useWorkspace } from '#features/common/hooks/use-workspace'
import { api } from '#lib/trpc/react'

import {
  USE_DEFAULT_MODEL_OPTION_VALUE,
  filterAvailableModelSelectModels,
  filterModelSelectModels,
  partitionModelSelectModels,
  resolveModelSelectDefaultPresentation,
} from './model-select-state.ts'

const providerLogos: Record<string, typeof ClaudeLogo> = {
  claude: ClaudeLogo,
  codex: OpenAILogo,
  opencode: OpenCodeLogo,
}

interface ModelSelectItem {
  id: string
  label: string
  providerId: string
  requiresMemberIntegration?: string
}

interface GroupedModelItems<T extends ModelSelectItem> {
  providerId: string
  group: string
  items: T[]
}

function groupModelItems<T extends ModelSelectItem>(
  items: readonly T[],
  providerLabels: ReadonlyMap<string, string>,
): GroupedModelItems<T>[] {
  const providerIds = Array.from(new Set(items.map((item) => item.providerId)))

  return providerIds.map((providerId) => ({
    providerId,
    group: providerLabels.get(providerId) ?? providerId,
    items: items.filter((item) => item.providerId === providerId),
  }))
}

interface ModelSelectProps {
  value: string[]
  onValueChange: (details: { value: string[] }) => void
  onUseDefault?: () => void
  useDefaultLabel?: string
  defaultModelId?: string
  isUsingDefault?: boolean
  disabled?: boolean
}

export function ModelSelect({
  value,
  onValueChange,
  onUseDefault,
  useDefaultLabel = 'Use blueprint defaults',
  defaultModelId,
  isUsingDefault = false,
  disabled,
}: ModelSelectProps) {
  const workspace = useWorkspace()
  const { data } = api.implementations.executionConfig.useQuery()
  const models = data?.models ?? []
  const providers = data?.providers ?? []
  const allowUseDefault = onUseDefault !== undefined
  const usingDefault = allowUseDefault && isUsingDefault
  const providerLabels = new Map(
    providers.map((provider) => [provider.id, provider.label]),
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [open, setOpen] = useState(false)

  // Subscription-backed models (e.g. OpenAI via ChatGPT) are visible only
  // when the viewer has connected that integration in this workspace. The
  // query is skipped entirely when no listed model needs one.
  const needsMemberIntegrations = models.some(
    (item) => item.requiresMemberIntegration !== undefined,
  )
  const { data: memberIntegrations } = api.memberIntegrations.list.useQuery(
    { workspaceId: workspace },
    { enabled: Boolean(workspace) && needsMemberIntegrations },
  )
  const connectedIntegrationIds = useMemo(
    () =>
      new Set(
        (memberIntegrations ?? [])
          .filter((item) => item.status === 'active')
          .map((item) => item.integrationId),
      ),
    [memberIntegrations],
  )
  const availableModels = useMemo(
    () => filterAvailableModelSelectModels({ models, connectedIntegrationIds }),
    [models, connectedIntegrationIds],
  )
  const filteredModels = useMemo(
    () =>
      filterModelSelectModels({
        models: availableModels,
        providers,
        query: searchQuery,
      }),
    [availableModels, providers, searchQuery],
  )
  const { primary: primaryModels, more: moreModels } = useMemo(
    () => partitionModelSelectModels(filteredModels),
    [filteredModels],
  )
  const primaryGroups = groupModelItems(primaryModels, providerLabels)
  const moreGroups = groupModelItems(moreModels, providerLabels)

  const selectedModel = availableModels.find((item) => item.id === value[0])
  const SelectedProviderLogo = selectedModel
    ? providerLogos[selectedModel.providerId]
    : undefined
  const defaultPresentation = resolveModelSelectDefaultPresentation({
    defaultModelId,
    isUsingDefault: usingDefault,
    models: availableModels,
  })
  const selectedModelId = usingDefault ? undefined : value[0]

  const closeMenu = () => {
    setOpen(false)
    setSearchQuery('')
  }

  const selectModel = (modelId: string) => {
    onValueChange({ value: [modelId] })
    closeMenu()
  }

  const selectDefault = () => {
    onUseDefault?.()
    closeMenu()
  }

  const renderModelItems = (items: readonly ModelSelectItem[]) =>
    items.map((item) => (
      <Menu.Item
        key={item.id}
        value={item.id}
        fontWeight={item.id === selectedModelId ? 'semibold' : 'normal'}
        onClick={() => selectModel(item.id)}
      >
        {item.label}
      </Menu.Item>
    ))

  const renderHarnessGroups = (groups: GroupedModelItems<ModelSelectItem>[]) =>
    groups.map(({ providerId, group, items }, groupIndex) => {
      const ProviderLogo = providerLogos[providerId]
      const isLastGroup = groupIndex === groups.length - 1
      const moreItems =
        moreGroups.find((candidate) => candidate.providerId === providerId)
          ?.items ?? []
      const selectedModelIsInMore = moreItems.some(
        (item) => item.id === selectedModelId,
      )

      return (
        <Menu.ItemGroup
          key={providerId}
          aria-label={group}
          borderBottomWidth={isLastGroup ? '0' : '1px'}
          borderColor="border"
          mb="1"
          pb="1"
        >
          <Text
            aria-hidden="true"
            display="flex"
            alignItems="center"
            gap="1.5"
            px="2"
            py="1.5"
            textStyle="xs"
            fontWeight="medium"
            color="fg.muted"
          >
            {ProviderLogo && <ProviderLogo boxSize="3.5" />}
            {group}
          </Text>
          {renderModelItems(items)}
          {moreItems.length > 0 && (
            <Menu.Root positioning={{ placement: 'right-start', gutter: 2 }}>
              <Menu.TriggerItem
                value={`more-models-${providerId}`}
                fontWeight={selectedModelIsInMore ? 'semibold' : 'normal'}
                justifyContent="space-between"
                css={{
                  '& > svg:last-child': {
                    color: 'fg.muted',
                    marginInlineStart: 'auto',
                    opacity: 0.6,
                  },
                }}
              >
                More
              </Menu.TriggerItem>
              <Menu.Content
                portalled
                zIndex="popover"
                minW="14rem"
                maxH="calc(var(--available-height) - 2rem)"
                overflowY="auto"
              >
                {renderModelItems(moreItems)}
              </Menu.Content>
            </Menu.Root>
          )}
        </Menu.ItemGroup>
      )
    })

  return (
    <Menu.Root
      open={open}
      onOpenChange={(details) => {
        setOpen(details.open)
        if (!details.open) setSearchQuery('')
      }}
      typeahead={false}
    >
      <Menu.Trigger asChild>
        <Button
          disabled={disabled}
          size="sm"
          variant="ghost"
          justifyContent="flex-start"
          gap="2"
          minW="0"
          maxW="100%"
          px="2.5"
          borderRadius="full"
          fontWeight="normal"
          bg="transparent"
          _hover={{ bg: 'bg.subtle' }}
        >
          {SelectedProviderLogo && <SelectedProviderLogo boxSize="4" />}
          {defaultPresentation.triggerLabel ? (
            <Text as="span" color="fg.muted" whiteSpace="nowrap">
              {defaultPresentation.triggerLabel}
            </Text>
          ) : (
            <Text
              as="span"
              data-selected={selectedModel ? '' : undefined}
              color="fg.muted"
              _selected={{ color: 'fg' }}
              truncate
            >
              {selectedModel?.label ?? 'Select model'}
            </Text>
          )}
        </Button>
      </Menu.Trigger>

      <Menu.Content portalled zIndex="popover" minW="14rem">
        <Box pb="1">
          <Input
            type="search"
            aria-label="Search models"
            placeholder="Search models"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            size="sm"
          />
        </Box>

        {allowUseDefault && (
          <>
            <Menu.Item
              value={USE_DEFAULT_MODEL_OPTION_VALUE}
              alignItems="flex-start"
              onClick={selectDefault}
            >
              <Box>
                <Text>{useDefaultLabel}</Text>
                <Text textStyle="xs" color="fg.muted" fontWeight="normal">
                  {defaultPresentation.optionDescription}
                </Text>
              </Box>
            </Menu.Item>
            <Menu.Separator />
          </>
        )}

        {filteredModels.length === 0 ? (
          <Text px="2" py="3" textStyle="sm" color="fg.muted">
            No models found
          </Text>
        ) : (
          renderHarnessGroups(primaryGroups)
        )}
      </Menu.Content>
    </Menu.Root>
  )
}
